/**
 * PushToggle - GameBoy-style toggle for push notifications (timbre rings).
 *
 * Shows the current permission state and lets the user enable/disable
 * push notifications. When the user enables, it triggers the browser
 * permission dialog and registers the subscription on the server.
 *
 * Mounted in SettingsScreen.
 */
import { useEffect, useState } from 'react';
import { CheckSprite, XSprite, BellSprite } from './Sprites';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  registerSubscriptionOnServer,
  unsubscribeAndRemoveFromServer,
} from '@/lib/push';
import { getApiBase } from '@/lib/api';

interface PushToggleProps {
  showToast: (msg: string, kind?: 'info' | 'success' | 'error') => void;
}

export function PushToggle({ showToast }: PushToggleProps) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSupported(isPushSupported());
    setPermission(getNotificationPermission());
  }, []);

  const apiBase = getApiBase();
  const token = typeof window !== 'undefined' ? localStorage.getItem('vicin-gb-token') : null;

  const handleEnable = async () => {
    setBusy(true);
    try {
      const sub = await subscribeToPush();
      if (!sub) {
        setPermission(getNotificationPermission());
        showToast('Permiso denegado', 'error');
        return;
      }
      if (!token) {
        showToast('Iniciá sesión antes de activar', 'error');
        return;
      }
      // Register on the server FIRST — only show "ACTIVADO" if the
      // server actually accepted the subscription. Otherwise the toggle
      // would lie (browsers always grant Notification.permission even
      // when the server-side registration silently fails, which led to
      // "ACTIVADO" UI with zero rows in push_subscriptions).
      const result = await registerSubscriptionOnServer(sub, token, apiBase);
      if (result.ok) {
        setPermission('granted');
        showToast('¡TIMBRE! Activado — vas a recibir avisos', 'success');
      } else {
        // Surface the actual failure to make debugging fast. The
        // generic "no se pudo registrar" was hiding auth issues (401),
        // schema mismatches (500), and CORS problems.
        const detail = result.status ? ` (HTTP ${result.status})` : '';
        showToast(`No se pudo registrar${detail}`, 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error activando notificaciones', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      if (token) {
        await unsubscribeAndRemoveFromServer(token, apiBase);
      }
      setPermission('default');
      showToast('Notificaciones desactivadas', 'info');
    } catch (err: any) {
      showToast(err?.message || 'Error desactivando', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="gb-menu-item" data-selected={false}>
        <span className="gb-menu-num">·</span>
        <span style={{ flex: 1, textAlign: 'left', fontSize: 6 }}>NO SOPORTADO</span>
        <span className="gb-menu-arrow"><XSprite size={8} /></span>
      </div>
    );
  }

  const isEnabled = permission === 'granted';
  const isDenied = permission === 'denied';

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <button
        onClick={isEnabled ? handleDisable : handleEnable}
        disabled={busy || isDenied}
        className="gb-menu-item"
        data-selected={false}
        style={{ flex: 1, opacity: busy ? 0.5 : 1 }}
        data-testid="settings-push-toggle"
      >
        <span className="gb-menu-num">·</span>
        <BellSprite size={10} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          {isDenied ? 'BLOQUEADO' : isEnabled ? 'ACTIVADO' : 'APAGADO'}
        </span>
        <span className="gb-menu-arrow">
          {isEnabled ? <CheckSprite size={8} /> : <XSprite size={8} />}
        </span>
      </button>
      {isDenied && (
        <span className="gb-pix gb-pix-xs" style={{ fontSize: 5, color: 'var(--gb-dark)', opacity: 0.7 }}>
          HABILITAR EN AJUSTES DEL NAVEGADOR
        </span>
      )}
    </div>
  );
}